import { DnsAttemptOutcome } from "@redotech/crdb-enums/dns/dns-attempt-outcome";
import { DnsCheckStatus } from "@redotech/crdb-enums/dns/dns-check-status";
import { DnsFailureClass } from "@redotech/crdb-enums/dns/dns-failure-class";
import { DnsRecordType } from "@redotech/crdb-enums/dns/dns-record-type";
import { DnsSurface } from "@redotech/crdb-enums/dns/dns-surface";
import * as z from "zod";
import {
  DnsAction,
  DnsPrerequisite,
  DnsRecord,
  DnsRecordClaim,
  DomainRequirements,
  DomainSummary,
  ProvisioningAttempt,
  RecordDetail,
  RecordStatus,
  SurfaceStatus,
  SurfaceSummary,
  ZoneCapability,
} from "./types";

/**
 * Wire shapes for the read API. Each is annotated with the type it mirrors, so
 * a field added to one and not the other is a compile error rather than a gap.
 */

const prerequisiteSchema_ = z.object({ code: z.string(), message: z.string() });
const prerequisiteSchema: z.ZodType<DnsPrerequisite> = prerequisiteSchema_;

const capabilityFactSchema = z.object({
  supported: z.boolean().nullable(),
  reason: z.string().optional(),
});

const zoneCapabilitySchema_ = z.object({
  provider: z.string().nullable(),
  checkedAt: z.date().nullable(),
  automation: capabilityFactSchema,
  delegation: capabilityFactSchema,
});
const zoneCapabilitySchema: z.ZodType<ZoneCapability> = zoneCapabilitySchema_;

const claimSchema_ = z.object({
  surface: z.enum(DnsSurface),
  kind: z.string(),
  required: z.boolean(),
  status: z.enum(RecordStatus),
  detail: z.enum(RecordDetail).optional(),
});
const claimSchema: z.ZodType<DnsRecordClaim> = claimSchema_;

const resolverErrorSchema = z.object({ code: z.string(), at: z.date() });

const askSchema = z.union([
  z.object({ kind: z.literal("publish"), requiredBy: z.date().optional() }),
  z.object({ kind: z.literal("optional") }),
  z.object({ kind: z.literal("held_until_setup_finishes") }),
  z.object({ kind: z.literal("published_by_redo") }),
  z.object({ kind: z.literal("blocked"), prerequisite: prerequisiteSchema }),
  z.object({ kind: z.literal("satisfied") }),
]);

const recordSchema_ = z.object({
  type: z.enum(DnsRecordType),
  host: z.string(),
  values: z.array(z.string()),
  adding: z.array(z.string()),
  ask: askSchema,
  ttlSeconds: z.number(),
  required: z.boolean(),
  actionable: z.boolean(),
  writeAction: z.union([z.enum(DnsAction), z.literal("none")]),
  managedBy: z.union([z.literal("redo"), z.literal("merchant")]),
  status: z.enum(RecordStatus),
  detail: z.enum(RecordDetail).optional(),
  prerequisite: prerequisiteSchema.optional(),
  conflictsWith: z.string().optional(),
  observedValues: z.array(z.string()).optional(),
  resolverError: resolverErrorSchema.optional(),
  newSinceLastAttempt: z.boolean().optional(),
  delegatedHost: z.string().optional(),
  onlyWhen: z.union([z.literal("delegated"), z.literal("direct")]).optional(),
  appliesTo: z
    .union([z.literal("direct"), z.literal("delegated"), z.literal("both")])
    .optional(),
  satisfies: z.array(claimSchema),
});
const recordSchema: z.ZodType<DnsRecord> = recordSchema_;

const surfaceSummarySchema_ = z.object({
  surface: z.enum(DnsSurface),
  needsSetup: z.boolean(),
  status: z.enum(SurfaceStatus),
  reason: z.string().optional(),
  prerequisites: z.array(prerequisiteSchema).optional(),
});
const surfaceSummarySchema: z.ZodType<SurfaceSummary> = surfaceSummarySchema_;

const attemptSchema_ = z.object({
  id: z.string(),
  initiatedBy: z.enum(DnsSurface),
  actor: z.union([z.literal("merchant"), z.literal("redo")]),
  initiatedAt: z.date(),
  requested: z.array(
    z.object({
      host: z.string(),
      type: z.enum(DnsRecordType),
      values: z.array(z.string()),
      adding: z.array(z.string()),
      required: z.boolean(),
      appliesTo: z
        .union([z.literal("direct"), z.literal("delegated"), z.literal("both")])
        .optional(),
      ttlSeconds: z.number().optional(),
    }),
  ),
  outcome: z.enum(DnsAttemptOutcome),
  settledAt: z.date().nullable(),
  failure: z
    .object({
      class: z.enum(DnsFailureClass),
      code: z.string(),
      message: z.string(),
    })
    .optional(),
});
const attemptSchema: z.ZodType<ProvisioningAttempt> = attemptSchema_;

const delegationOfferSchema = z.object({
  zone: z.string(),
  surface: z.enum(DnsSurface),
  nameservers: z.array(z.string()),
  creates: z.array(
    z.object({
      kind: z.string(),
      type: z.enum(DnsRecordType),
      host: z.string(),
    }),
  ),
});

export const domainSummarySchema_ = z.object({
  domain: z.string(),
  ownershipVerified: z.boolean(),
  zone: zoneCapabilitySchema,
});
export const domainSummarySchema: z.ZodType<DomainSummary> =
  domainSummarySchema_;

export const domainRequirementsSchema_ = z.object({
  domain: z.string(),
  lastObservedAt: z.date().nullable(),
  ownershipVerified: z.boolean(),
  zone: zoneCapabilitySchema,
  lastAttempt: attemptSchema.optional(),
  delegationOffer: delegationOfferSchema.optional(),
  delegatedZones: z.array(z.string()),
  surfaces: z.array(surfaceSummarySchema),
  records: z.array(recordSchema),
});
export const domainRequirementsSchema: z.ZodType<DomainRequirements> =
  domainRequirementsSchema_;

/** Codes a caller may branch on. Open by design — surfaces name their own. */
export const checkStatusSchema = z.enum(DnsCheckStatus);
